import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import firebase from "firebase/app";
import "firebase/firestore";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  Stack,
  Avatar,
  CircularProgress,
  IconButton
} from "@mui/material";
import { CalendarMonth, Psychology, WarningAmber } from "@mui/icons-material";
import styled from "styled-components";
import { openCalendly } from "../../utils/calendly";

const BRAND = {
  primary: "#92d1dc",
  secondary: "#bfd56c",
  text: "#1D1D1F",
  textSecondary: "#86868B",
};

const StyledDialog = styled(Dialog)`
  & .MuiPaper-root {
    border-radius: 32px;
    padding: 24px;
    max-width: 480px;
    background: #fff;
    box-shadow: 0 24px 60px rgba(0,0,0,0.15);
  }
`;

const CoachCard = styled(Box)`
  padding: 16px;
  border-radius: 20px;
  border: 1px solid #E5E5EA;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${BRAND.secondary};
    background: rgba(191, 213, 108, 0.05);
  }
`;

// Même règle que le courriel de bienvenue (member_welcome.js, PRODUCT_CALENDLY_FIELDS) :
// chaque coach a un lien Calendly par produit, choisi d'après ce que le membre a réellement.
const PRODUCT_CALENDLY_FIELDS = {
  sprint:  { eval: "sprintCalendlyUrl",  onboardingOnly: "sprintOnboardingOnlyCalendlyUrl" },
  hybrid:  { eval: "hybridCalendlyUrl",  onboardingOnly: "hybridOnboardingOnlyCalendlyUrl" },
  private: { eval: "privateCalendlyUrl", onboardingOnly: "privateOnboardingOnlyCalendlyUrl" },
};

const productTypeOf = (profile) => {
  if (!profile || profile.packageType === "BUSINESS_CONVERSATION_LAB") return null;
  const hasSprint = !!profile.bootCampStartDate;
  const hasPrivate = (Array.isArray(profile.hoursPurchased) ? profile.hoursPurchased : [])
    .filter((h) => !h.retired)
    .reduce((sum, h) => sum + (parseFloat(h.numberOfHours) || 0), 0) > 0;
  return hasSprint && hasPrivate ? "hybrid" : hasSprint ? "sprint" : hasPrivate ? "private" : null;
};

const normalizeCalendlyUrl = (val) => {
  if (!val) return null;
  if (val.startsWith("http://") || val.startsWith("https://")) return val;
  if (val.startsWith("/")) return `https://calendly.com${val}`;
  return `https://calendly.com/${val}`;
};

const EvaluationBlockingModal = ({ open, profile, auth, phase = "eval", mode = "eval_onboarding", onDismiss }) => {
  const { t } = useTranslation();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFallback, setIsFallback] = useState(false);

  const isMastermind = profile?.packageType === "MASTERMIND";
  const isFirstClassPhase = phase === "first_class";
  // `mode` s'applique au phase="eval" pour Mastermind ET non-Mastermind :
  // même pool de coachs (isEvaluator / isMastermindEvaluator), seul le lien
  // Calendly change selon ce qu'il reste à faire au membre (l'onboarding
  // Mastermind — appel avec Hilma/Stavros — n'avait encore aucune modale).
  const isOnboardingOnly = !isFirstClassPhase && mode === "onboarding_only";
  const productFields = isMastermind ? null : PRODUCT_CALENDLY_FIELDS[productTypeOf(profile)] || null;

  useEffect(() => {
    const fetchCoaches = async () => {
      setLoading(true);
      setIsFallback(false);
      try {
        if (isMastermind && isFirstClassPhase) {
          const snapshot = await firebase.firestore()
            .collection('professor')
            .where('isMastermindFirstClassCoach', '==', true)
            .where('retired', '==', false)
            .get();
          setCoaches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } else if (isMastermind) {
          // Mastermind eval: show isMastermindEvaluator coaches
          const snapshot = await firebase.firestore()
            .collection('professor')
            .where('isMastermindEvaluator', '==', true)
            .where('retired', '==', false)
            .get();
          setCoaches(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } else {
          const levelValues = { "A1": 1, "A2": 2, "B1": 3, "B2": 4, "C1": 5, "C2": 6 };
          const studentLevel = profile.Level || profile.level || "A1";
          const studentLang = profile.learningLanguage || profile.language || "en";

          const filterValid = (docs) => docs.filter(c => {
            if (!c) return false;
            if (c.retired) return false;
            if (c.isEvaluator !== true) return false;
            const canEvalLang = c.evalLanguages ? c.evalLanguages.includes(studentLang) : true;
            if (!canEvalLang) return false;
            const coachMin = levelValues[c.minEvalLevel] || 1;
            const studentValue = levelValues[studentLevel] || 1;
            if (coachMin > studentValue) return false;
            return true;
          });

          const snapshot = await firebase.firestore()
            .collection('professor')
            .where('isEvaluator', '==', true)
            .get();

          const allEvalData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setCoaches(filterValid(allEvalData));
          setIsFallback(false);
        }
      } catch (error) {
        console.error("Error fetching evaluators:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchCoaches();
    }
  }, [open, isMastermind, isFirstClassPhase, profile?.language, profile?.learningLanguage, profile?.Level]);

  if (!open) return null;

  return (
    <StyledDialog
      open={open}
      disableEscapeKeyDown={!isFirstClassPhase}
      onClose={isFirstClassPhase ? onDismiss : () => {}}
      aria-labelledby="evaluation-blocking-title"
    >
      <DialogContent sx={{ textAlign: "center", p: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'rgba(146, 209, 220, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <Psychology sx={{ fontSize: 40, color: BRAND.primary }} />
          </Box>
          <Typography variant="h4" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.02em' }}>
            {isFirstClassPhase
              ? t("evaluation.first_class_title", "Book your first strategic session")
              : isOnboardingOnly
                ? t("evaluation.onboarding_only_title", "One last step !")
                : t("evaluation.modal_title", "Presque prêt !")}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ fontSize: '1.1rem', lineHeight: 1.5 }}>
            {isFirstClassPhase
              ? t("evaluation.first_class_desc", "Your evaluation is complete! Now book your mandatory first session with one of our Ellipse Mastermind Experts.")
              : isOnboardingOnly
                ? t("evaluation.onboarding_only_desc", "Your evaluation is already complete — book your onboarding call so we can get you fully set up.")
                : t("evaluation.modal_desc", "Ton évaluation initiale doit être complétée avant d'accéder à la plateforme.")}
          </Typography>
        </Box>

        <Typography variant="overline" fontWeight={700} color="textSecondary" sx={{ mb: 2, display: 'block' }}>
          {isFirstClassPhase
            ? t("evaluation.choose_first_class_coach", "Ellipse Mastermind Facilitators")
            : isOnboardingOnly
              ? t("evaluation.choose_onboarding_coach", "Choisis un moment avec ton coach")
              : t("evaluation.choose_evaluator", "Choisis un évaluateur disponible")}
        </Typography>

        {loading ? (
          <CircularProgress size={32} sx={{ my: 4 }} />
        ) : (
          <Stack spacing={2} sx={{ mb: 2 }}>
            {coaches.length > 0 ? (
              coaches.map((coach) => {
                const calendlyUrl = normalizeCalendlyUrl(
                  isFirstClassPhase
                    ? coach.mastermindFirstClassCalendlyUrl
                    : isMastermind
                      ? isOnboardingOnly
                        ? (coach.mastermindOnboardingOnlyCalendlyUrl || coach.mastermindCalendlyUrl)
                        : coach.mastermindCalendlyUrl
                      : isOnboardingOnly
                        ? ((productFields && coach[productFields.onboardingOnly]) || coach.onboardingOnlyCalendlyUrl || coach.evalCalendlyUrl || coach.urlCalendly || coach.calendly)
                        : ((productFields && coach[productFields.eval]) || coach.evalCalendlyUrl || coach.urlCalendly || coach.calendly)
                );
                return (
                  <CoachCard key={coach.id}>
                    <Avatar
                      src={coach.pictureUrl || coach.photoUrl || coach.avatarUrl || coach.photoURL}
                      sx={{ width: 56, height: 56, bgcolor: '#F5F5F7' }}
                    >
                      {coach.firstname?.[0] || coach.name?.[0]}
                    </Avatar>
                    <Box flex={1} textAlign="left">
                      <Typography variant="subtitle1" fontWeight={700}>
                        {coach.firstname}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {isFirstClassPhase ? t("evaluation.facilitator_label", "Ellipse Mastermind Facilitators") : "Ellipse Language Expert"}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CalendarMonth />}
                      disabled={!calendlyUrl}
                      onClick={() => {
                        if (!calendlyUrl) return;
                        const sep = calendlyUrl.includes('?') ? '&' : '?';
                        const safeUrl = `${calendlyUrl}${sep}hide_gdpr_banner=1`;
                        openCalendly({
                          url: safeUrl,
                          prefill: { name: `${profile?.firstname || ''} ${profile?.lastname || ''}`.trim(), email: profile?.email },
                          utm: { source: auth.uid, medium: coach.id },
                          pageSettings: { hideGdprBanner: true, hideLandingPageDetails: true, primaryColor: 'B0D50B' }
                        });
                      }}
                      sx={{
                        bgcolor: BRAND.secondary,
                        color: '#0d1b0a',
                        fontWeight: 700,
                        borderRadius: '12px',
                        '&:hover': { bgcolor: '#a3b856' }
                      }}
                    >
                      {t("evaluation.book_btn", "Réserver")}
                    </Button>
                  </CoachCard>
                );
              })
            ) : (
              <Box p={3} bgcolor="#FFF4F4" borderRadius="20px" border="1px solid #FFDada">
                <WarningAmber color="error" sx={{ mb: 1 }} />
                <Typography variant="body2" color="error" fontWeight={600}>
                  {t("evaluation.no_coach_assigned", "Aucun coach assigné. Contacte le support.")}
                </Typography>
              </Box>
            )}
          </Stack>
        )}

        {isFirstClassPhase && (
          <Button
            variant="outlined"
            onClick={onDismiss}
            sx={{ mt: 2, mr: 1, textTransform: 'none', fontWeight: 700, borderRadius: '12px' }}
          >
            {t("evaluation.ok_btn", "OK, access portal")}
          </Button>
        )}

        <Button
          onClick={async () => await firebase.auth().signOut()}
          sx={{ mt: isFirstClassPhase ? 2 : 3, textTransform: 'none', color: BRAND.textSecondary, fontWeight: 600 }}
        >
          {t("common.logout", "Log out")}
        </Button>
      </DialogContent>
    </StyledDialog>
  );
};

export default EvaluationBlockingModal;
